import * as React from 'react';

import { DescriptionPage_SelfServeConfiguration_ModuleName as ModuleNames } from '__generated__/graphql-types';

import GoogleCertificateEpicClient from 'bundles/epic/clients/GoogleCertificate';
import ArticlesRecommendationsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ArticlesRecommendationsModuleRenderer';
import CareerCertificateModuleRenderer from 'bundles/unified-description-page-common/module-renderers/CareerCertificateModuleRenderer';
import CrossSellModuleRenderer from 'bundles/unified-description-page-common/module-renderers/CrossSellModuleRenderer';
import LearnerIncentiveModuleRenderer from 'bundles/unified-description-page-common/module-renderers/LearnerIncentiveModuleRenderer';
import PageBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/PageBannerModuleRenderer';
import TestimonialsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/TestimonialsModuleRenderer';
import ConsumerBreadcrumbsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/breadcrumbs/ConsumerBreadcrumbsModuleRenderer';
import CareerOutcomesModuleRenderer from 'bundles/unified-description-page-common/module-renderers/career-outcomes/ConsumerProfessionalCertificateCareerOutcomesModuleRenderer';
import FAQsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/faqs/FAQsModuleRenderer';
import ProductOverviewModuleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/ConsumerSpecializationProductOverviewModuleRenderer';
import SyllabusModuleRenderer from 'bundles/unified-description-page-common/module-renderers/syllabus/SpecializationSyllabusModuleRenderer';
import UpsellModuleRenderer from 'bundles/unified-description-page-common/module-renderers/upsell/ConsumerUpsellModuleRenderer';
import type {
  ConsumerSpecializationMarketingPageFieldsFragment,
  SelfServeConfigCrossSellModuleFragment,
  SelfServeConfigPageBannerModuleFragment,
  SelfServeConfigurationFragment,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationMarketingPageQuery';
import type { ProductOverviewModuleConfig } from 'bundles/unified-description-page-common/types/moduleConfig';
import { ProductDetailItemNames, UpsellCardNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import useIsSimplifiedGoogleXdp from 'bundles/unified-description-page-common/utils/useIsSimplifiedGoogleXdp';

type Props = {
  pageData: ConsumerSpecializationMarketingPageFieldsFragment;
  pageConfig?: SelfServeConfigurationFragment | null;
};

const Renderer = ({ pageData, pageConfig }: Props) => {
  const removeLinksAboveFold = GoogleCertificateEpicClient.get('removeLinksAboveFold');

  const isSimplifiedGoogleXdp = useIsSimplifiedGoogleXdp();

  if (!pageConfig || !pageConfig.modules.length) {
    return null;
  }

  const modulesToRender = pageConfig.modules.map((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.PageBanner:
        return (
          <PageBannerModuleRenderer
            key={moduleConfig.name}
            config={moduleConfig as SelfServeConfigPageBannerModuleFragment}
          />
        );
      case ModuleNames.ProductOverview:
        return (
          <ProductOverviewModuleRenderer
            pageData={pageData}
            config={
              {
                ...moduleConfig,
                removeLinksAboveFold,
                hideOverviewDescription: isSimplifiedGoogleXdp,
                productDetailItems: isSimplifiedGoogleXdp
                  ? []
                  : [ProductDetailItemNames.ShareableCertificate, ProductDetailItemNames.LanguagesWithMLInfo],
              } as ProductOverviewModuleConfig
            }
            key={moduleConfig.name}
          />
        );
      case ModuleNames.CareerOutcomes:
        return <CareerOutcomesModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.CareerCertificate:
        return <CareerCertificateModuleRenderer key={moduleConfig.name} />;
      case ModuleNames.LearnerIncentive:
        return <LearnerIncentiveModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Syllabus:
        return <SyllabusModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Testimonials:
        return <TestimonialsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.ArticlesRecommendations:
        return <ArticlesRecommendationsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Upsell:
        return (
          <UpsellModuleRenderer
            key={moduleConfig.name}
            config={{ upsellCards: [UpsellCardNames.CourseraPlusUpsellCard] }}
          />
        );
      case ModuleNames.Faqs:
        return <FAQsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Breadcrumbs:
        return (
          <ConsumerBreadcrumbsModuleRenderer
            pageData={pageData}
            config={{ removeLinksAboveFold, hideCtas: true }}
            key={moduleConfig.name}
          />
        );
      case ModuleNames.CrossSell:
        return (
          <CrossSellModuleRenderer
            config={moduleConfig as SelfServeConfigCrossSellModuleFragment}
            key={moduleConfig.name}
          />
        );
      default:
        return null;
    }
  });

  return <>{modulesToRender}</>;
};

export default Renderer;
